import { FileText, Info, Play, Search, SkipForward, X } from "lucide-react";
import { useMeetingStore } from "../../store/meeting.store";
import { socket } from "../mediasoup/socket";
import { useEffect, useRef, useState, useCallback } from "react";
import toast from "react-hot-toast";
import api from "../../lib/api";
import HearingTimer, { getHearingTimes } from "./HearingTimer";
import { recordingSocket } from "../mediasoup/recordingSocket";
import FloatingPortal from "../common/FloatingPortal";

const updateHearingStatus = (cases: any[], cause: any) => {
  return cases.map((c: any) => {
    if (c.cnr === cause?.previousCnr && c.hearingStatus !== "Passover") {
      return { ...c, hearingStatus: "Completed" };
    }

    if (c.cnr === cause.cnr) {
      return { ...c, hearingStatus: "Active" };
    }

    return c;
  });
};

const getId = (value: any) => {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

const getCauseRoomId = (cause: any) => {
  if (!cause) return "";
  return getId(
    cause.courtType === "High Court" ? cause.benchRoom : cause.courtRoom,
  );
};

const getCauseKey = (cause: any) => getId(cause?._id) || cause?.cnr || "";

const mergeCause = (cases: any[], cause: any, prepend = false) => {
  if (!cause) return cases;
  const nextKey = getCauseKey(cause);
  if (!nextKey) return cases;

  let found = false;
  const updated = cases.map((item: any) => {
    const isSame = getCauseKey(item) === nextKey || item?.cnr === cause?.cnr;
    if (!isSame) return item;
    found = true;
    return { ...item, ...cause };
  });

  if (found) return updated;
  return prepend ? [cause, ...updated] : [...updated, cause];
};

const removeCause = (cases: any[], payload: any) => {
  const deletedId = getId(payload?.causeId);
  const deletedCnr = payload?.cnr;
  return cases.filter((cause: any) => {
    if (deletedId && getId(cause?._id) === deletedId) return false;
    if (deletedCnr && cause?.cnr === deletedCnr) return false;
    return true;
  });
};

const toDateKey = (value: any) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [day, month, year] = value.split("-");
    return `${year}-${month}-${day}`;
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

// Local calendar date; toISOString() is UTC and gives yesterday before 05:30 IST.
const getLocalDateKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const isTodaysCause = (cause: any) => {
  return toDateKey(cause?.hearingDate) === getLocalDateKey();
};

const matchesSearch = (cause: any, query: string) => {
  if (!query) return true;
  const normalizedQuery = query.toLowerCase();
  return [
    cause?.cnr,
    cause?.caseNumber,
    cause?.petitioner,
    cause?.respondent,
  ].some((value) =>
    String(value || "")
      .toLowerCase()
      .includes(normalizedQuery),
  );
};

const MeetingCauseList = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [listView, setListView] = useState<"daily" | "passover">("daily");
  const [expandedCase, setExpandedCase] = useState<string | null>(null);
  const [pendingPassover, setPendingPassover] = useState<string | null>(null);
  const [confirmPassover, setConfirmPassover] = useState<any>(null);
  const [pendingStart, setPendingStart] = useState<string | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [allResults, setAllResults] = useState<any[]>([]);
  const [allCauses, setAllCauses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingRef = useRef(false);
  const causePageRef = useRef(1);
  const searchPageRef = useRef(1);
  const hasMoreCausesRef = useRef(true);
  const hasMoreResultsRef = useRef(true);
  const debouncedQueryRef = useRef("");
  const setCauses = useMeetingStore((s) => s.setCauses);
  const selectedCase = useMeetingStore((s) => s.selectedCase);
  const setSelectedCase = useMeetingStore((s) => s.setSelectedCase);
  const setRecording = useMeetingStore((s) => s.setRecording);
  const setActiveRecordingCnr = useMeetingStore((s) => s.setActiveRecordingCnr);
  const setRecordingStartedAt = useMeetingStore((s) => s.setRecordingStartedAt);
  const setIsStreaming = useMeetingStore((s) => s.setIsStreaming);
  const setStreamingStartedAt = useMeetingStore((s) => s.setStreamingStartedAt);
  const userData = useMeetingStore((s) => s.userData);
  const courtRoomData = useMeetingStore((s) => s.courtRoomData);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTime = useRef(0);
  const requestIdRef = useRef(0);
  const pendingRecordingStartCnrRef = useRef<string | null>(null);
  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const [serverCounts, setServerCounts] = useState<{
    daily: number;
    passover: number;
  } | null>(null);

  const sourceCauses = debouncedQuery ? allResults : allCauses;
  const displayCauses = sourceCauses.filter((cause) =>
    listView === "passover"
      ? cause.hearingStatus === "Passover"
      : cause.hearingStatus !== "Passover",
  );
  const hasMore = debouncedQuery
    ? hasMoreResultsRef.current
    : hasMoreCausesRef.current;
  // Tab badges: live client count once whole list loaded, else server totals
  const loadedPassover = allCauses.filter(
    (c) => c.hearingStatus === "Passover",
  ).length;
  const viewCounts =
    hasMoreCausesRef.current && serverCounts
      ? serverCounts
      : {
          daily: allCauses.length - loadedPassover,
          passover: loadedPassover,
        };

  const fetchCauses = useCallback(
    async (pageNum: number) => {
      if (isFetchingRef.current && pageNum !== 1) return;
      if (!hasMoreCausesRef.current && pageNum !== 1) return;
      const requestId = ++requestIdRef.current;
      isFetchingRef.current = true;
      pageNum === 1 ? setLoading(true) : setIsFetchingMore(true);
      try {
        const res = await api.get(`/api/judge/cases`, {
          params: { page: pageNum, limit: 20 },
        });
        if (requestId !== requestIdRef.current) return;
        const newData: any[] = res.data?.data ?? [];
        if (res.data?.counts) setServerCounts(res.data.counts);
        if (res.data?.serverNow) {
          const offset = res.data.serverNow - Date.now();
          setServerOffsetMs(offset);
          console.log("[TIMER][FE][STEP2] serverOffsetMs", offset);
        }

        const hasMore: boolean = res.data?.pagination?.hasMore ?? false;
        causePageRef.current = pageNum;
        hasMoreCausesRef.current = hasMore;
        if (pageNum === 1) {
          setCauses(newData);
        }
        setAllCauses((prev) => {
          if (pageNum === 1) return newData;
          const seen = new Set(prev.map((c: any) => getCauseKey(c)));
          return [
            ...prev,
            ...newData.filter((c: any) => !seen.has(getCauseKey(c))),
          ];
        });
      } catch {
        if (requestId === requestIdRef.current)
          toast.error("Unable to load the cause list. Please try again.");
      } finally {
        if (requestId === requestIdRef.current) {
          isFetchingRef.current = false;
          setLoading(false);
          setIsFetchingMore(false);
        }
      }
    },
    [setCauses],
  );

  const fetchSearch = useCallback(async (query: string, pageNum: number) => {
    if (isFetchingRef.current && pageNum !== 1) return;
    if (!hasMoreResultsRef.current && pageNum !== 1) return;
    const requestId = ++requestIdRef.current;
    isFetchingRef.current = true;
    pageNum === 1 ? setLoading(true) : setIsFetchingMore(true);
    try {
      const res = await api.get(`/api/judge/cases`, {
        params: { cnr: query, page: pageNum, limit: 20 },
      });
      const newData: any[] = res.data?.data ?? [];
      if (
        requestId !== requestIdRef.current ||
        query !== debouncedQueryRef.current
      )
        return;
      hasMoreResultsRef.current =
        res.data?.pagination?.hasMore ?? newData.length === 20;
      setAllResults((prev) => {
        if (pageNum === 1) return newData;
        const seen = new Set(prev.map((c: any) => c.cnr));
        return [...prev, ...newData.filter((c: any) => !seen.has(c.cnr))];
      });
      searchPageRef.current = pageNum;
    } catch {
      if (requestId === requestIdRef.current)
        toast.error("Unable to search cases. Please try again.");
    } finally {
      if (requestId === requestIdRef.current) {
        isFetchingRef.current = false;
        setLoading(false);
        setIsFetchingMore(false);
      }
    }
  }, []);

  const refreshTimes = useCallback(async (cnr?: string) => {
    try {
      const res = await api.get(`/api/judge/cases`, {
        params: cnr ? { cnr, page: 1, limit: 20 } : { page: 1, limit: 20 },
      });
      const fresh: any[] = res.data?.data ?? [];
      if (res.data?.counts) setServerCounts(res.data.counts);
      if (res.data?.serverNow) {
        setServerOffsetMs(res.data.serverNow - Date.now());
      }
      console.log(
        "[TIMER][FE][STEP2.1] refreshTimes",
        fresh.map((c) => ({
          cnr: c.cnr,
          caseStartAt: c.caseStartAt,
          streamStartAt: c.streamStartAt,
          streamEndAt: c.streamEndAt,
          videoDuration: c.videoDuration,
          hearingStatus: c.hearingStatus,
        })),
      );
      const patch = (items: any[]) =>
        items.map((item) => {
          const f = fresh.find((x) => x.cnr === item.cnr);
          return f
            ? {
                ...item,
                caseStartAt: f.caseStartAt,
                caseEndAt: f.caseEndAt,
                streamStartAt: f.streamStartAt,
                streamEndAt: f.streamEndAt,
                videoDuration: f.videoDuration,
                hearingStatus: f.hearingStatus,
              }
            : item;
        });
      setAllCauses(patch);
      setAllResults(patch);
    } catch (e) {
      console.error("[TIMER][FE][STEP2.1] refreshTimes failed", e);
    }
  }, []);

  // Rejoin after a crash / shutdown: today's case is still Active in the DB
  // (timer running) but this client lost its selected case, so the card
  // offered Start and recording stayed locked. Resume it without restarting
  // — no judge-select-case, which would reset the hearing timer.
  useEffect(() => {
    if (selectedCase || !courtRoomData?.courtRoomId) return;
    const active = allCauses.find((c) => c?.hearingStatus === "Active");
    if (!active) return;
    setSelectedCase(active);
    socket.emit("resume-active-case", {
      cnr: active.cnr,
      courtRoomId: courtRoomData.courtRoomId,
    });
  }, [allCauses, selectedCase, courtRoomData?.courtRoomId, setSelectedCase]);

  useEffect(() => {
    if (!confirmPassover) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setConfirmPassover(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [confirmPassover]);

  useEffect(() => {
    const onStatus = (d: any) => {
      console.log("[TIMER][FE][STEP2.1] stream-status", d);
      if (
        d?.status === "live" ||
        d?.status === "scheduled" ||
        d?.status === "draining"
      ) {
        refreshTimes();
      }
    };
    const onStop = (d: any) => {
      console.log("[TIMER][FE][STEP2.1] stream_stop", d);
      refreshTimes();
    };
    recordingSocket.on("stream-status", onStatus);
    recordingSocket.on("stream_stop", onStop);
    return () => {
      recordingSocket.off("stream-status", onStatus);
      recordingSocket.off("stream_stop", onStop);
    };
  }, [refreshTimes]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const q = searchQuery.trim();
      debouncedQueryRef.current = q;
      setDebouncedQuery(q);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setAllCauses([]);
    setAllResults([]);
    hasMoreCausesRef.current = true;
    hasMoreResultsRef.current = true;
    causePageRef.current = 1;
    searchPageRef.current = 1;
    if (debouncedQuery) {
      fetchSearch(debouncedQuery, 1);
    } else {
      fetchCauses(1);
    }
    return () => {
      requestIdRef.current += 1;
      isFetchingRef.current = false;
    };
  }, [debouncedQuery, fetchCauses, fetchSearch]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollTime.current < 300) return; // 300ms throttle
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight < scrollHeight - 20) return;
      if (isFetchingRef.current) return;
      lastScrollTime.current = now;
      const query = debouncedQueryRef.current;

      if (query) {
        if (hasMoreResultsRef.current)
          fetchSearch(query, searchPageRef.current + 1);
        return;
      }

      if (hasMoreCausesRef.current) {
        fetchCauses(causePageRef.current + 1);
      }
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [fetchCauses, fetchSearch]);

  const handleCaseSelect = (cause: any) => {
    if (!cause || pendingPassover || pendingRecordingStartCnrRef.current)
      return;
    if (!socket.connected) {
      toast.error("Please wait for the meeting to reconnect.");
      return;
    }
    const previousCnr =
      selectedCase?.cnr !== cause.cnr ? selectedCase?.cnr : undefined;
    const role = userData?.role === "Reader" ? "Reader" : "Judge";
    pendingRecordingStartCnrRef.current = cause.cnr;
    setPendingStart(cause.cnr);

    // Mark previous case as Completed in DB before switching
    if (previousCnr && selectedCase?.hearingStatus !== "Passover") {
      socket.emit("update-hearing-status", {
        cnr: previousCnr,
        courtRoomId: courtRoomData.courtRoomId,
        hearingStatus: "Completed",
        userId: userData._id,
      });

      socket.emit("recording-action", {
        cnr: previousCnr,
        roomId: courtRoomData.courtRoomId,
        status: "stop",
        userId: userData._id,
      });
    }

    socket.timeout(10000).emit(
      "judge-select-case",
      {
        cnr: cause.cnr,
        previousCnr,
        userId: userData._id,
        courtRoomId: courtRoomData.courtRoomId,
        actionBy: userData?._id,
        role,
        cause,
      },
      (error: Error | null, callbackData: any) => {
        if (
          (error || !callbackData?.status) &&
          pendingRecordingStartCnrRef.current === cause.cnr
        ) {
          pendingRecordingStartCnrRef.current = null;
          setPendingStart(null);
          toast.error(
            callbackData?.message ||
              "Unable to start the case. Please try again.",
            { id: "call-back-select-case" },
          );
        }
      },
    );
  };

  const handlePassover = (cause: any) => {
    if (pendingPassover || pendingRecordingStartCnrRef.current) return;
    if (!socket.connected) {
      toast.error("Please wait for the meeting to reconnect.");
      return;
    }
    setPendingPassover(cause.cnr);
    socket.timeout(10000).emit(
      "update-hearing-status",
      {
        cnr: cause.cnr,
        causeId: getId(cause._id),
        courtRoomId: courtRoomData.courtRoomId,
        hearingStatus: "Passover",
        userId: userData._id,
      },
      (
        error: Error | null,
        response: { status: boolean; message?: string },
      ) => {
        setPendingPassover(null);
        if (error || !response?.status) {
          toast.error(
            response?.message ||
              "Unable to pass over the case. Please try again.",
          );
          return;
        }
        if (useMeetingStore.getState().selectedCase?.cnr === cause.cnr) {
          socket.emit("recording-action", {
            cnr: cause.cnr,
            roomId: courtRoomData.courtRoomId,
            status: "stop",
            reason: "passover",
            userId: userData._id,
          });
        }
        // Stop timer now; server caseEndAt replaces this on refresh
        const passedOverCause = {
          ...cause,
          hearingStatus: "Passover",
          ...(cause.caseStartAt && !cause.caseEndAt
            ? { caseEndAt: new Date(Date.now() + serverOffsetMs).toISOString() }
            : {}),
        };
        setCauses((prev: any) => mergeCause(prev, passedOverCause));
        setAllCauses((prev) => mergeCause(prev, passedOverCause));
        setAllResults((prev) =>
          prev.map((item) =>
            getCauseKey(item) === getCauseKey(cause) ? passedOverCause : item,
          ),
        );
        if (useMeetingStore.getState().selectedCase?.cnr === cause.cnr) {
          setSelectedCase(passedOverCause);
        }
        setListView("passover");
        toast.success("Case moved to Passover");
      },
    );
  };

  const handleRemovePassover = (cause: any) => {
    if (pendingPassover || pendingRecordingStartCnrRef.current) return;
    if (!socket.connected) {
      toast.error("Please wait for the meeting to reconnect.");
      return;
    }
    setPendingPassover(cause.cnr);
    socket.timeout(10000).emit(
      "update-hearing-status",
      {
        cnr: cause.cnr,
        causeId: getId(cause._id),
        courtRoomId: courtRoomData.courtRoomId,
        hearingStatus: "Pending",
        userId: userData._id,
      },
      (
        error: Error | null,
        response: { status: boolean; message?: string },
      ) => {
        setPendingPassover(null);
        if (error || !response?.status) {
          toast.error(
            response?.message ||
              "Unable to remove the case from Passover. Please try again.",
          );
          return;
        }
        const restored = { ...cause, hearingStatus: "Pending" };
        setCauses((prev: any) => mergeCause(prev, restored));
        setAllCauses((prev) => mergeCause(prev, restored));
        setAllResults((prev) =>
          prev.map((item) =>
            getCauseKey(item) === getCauseKey(cause) ? restored : item,
          ),
        );
        toast.success("Case moved back to Daily List");
      },
    );
  };

  useEffect(() => {
    const handleCaseSelected = (cause: any) => {
      const eventCourtRoomId = getId(cause?.courtRoomId);
      if (
        eventCourtRoomId &&
        courtRoomData?.courtRoomId &&
        eventCourtRoomId !== getId(courtRoomData.courtRoomId)
      ) {
        return;
      }

      setCauses((prev: any) => updateHearingStatus(prev, cause));
      setAllCauses((prev) => updateHearingStatus(prev, cause));
      setAllResults((prev) => updateHearingStatus(prev, cause));

      const isOwnStart =
        cause?.cnr && cause.cnr === pendingRecordingStartCnrRef.current;

      setSelectedCase({ ...cause.cause, hearingStatus: "Active" });
      setPendingStart(null);
      // Case start written to DB before "case-selected" emit — pull caseStartAt so timer starts now
      if (cause?.cnr) refreshTimes(cause.cnr);
      if (cause?.previousCnr) refreshTimes(cause.previousCnr);

      if (isOwnStart) {
        const startedAt = Date.now();
        pendingRecordingStartCnrRef.current = null;
        // Started case leaves Passover list — show it in Daily
        setListView("daily");
        setRecording("start");
        setActiveRecordingCnr(cause.cnr);
        setRecordingStartedAt(startedAt);
        setIsStreaming(false);
        setStreamingStartedAt(null);
        socket.emit("recording-action", {
          cnr: cause.cnr,
          roomId: courtRoomData.courtRoomId,
          status: "start",
          userId: userData._id,
          mode: "rec-only",
          startedAt,
        });
      } else if (pendingRecordingStartCnrRef.current) {
        pendingRecordingStartCnrRef.current = null;
      }
    };
    const handleHearingStatusUpdated = (payload: any) => {
      if (
        payload?.courtRoomId &&
        getId(payload.courtRoomId) !== getId(courtRoomData?.courtRoomId)
      )
        return;
      const syncStatus = (items: any[]) =>
        items.map((item: any) =>
          item?.cnr === payload?.cnr
            ? { ...item, hearingStatus: payload.hearingStatus }
            : item,
        );

      setCauses((prev: any) => syncStatus(Array.isArray(prev) ? prev : []));
      setAllCauses(syncStatus);
      setAllResults(syncStatus);
      // Completed / Passover / bench left stamps caseEndAt — pull it so the final time shows
      if (payload?.cnr) refreshTimes(payload.cnr);

      if (selectedCase?.cnr === payload?.cnr) {
        setSelectedCase({
          ...selectedCase,
          hearingStatus: payload.hearingStatus,
        });
        if (payload.hearingStatus === "Passover") {
          setRecording("stop");
          setActiveRecordingCnr(null);
          setRecordingStartedAt(null);
          setIsStreaming(false);
          setStreamingStartedAt(null);
        }
      }
    };
    const handleCauseListCreated = (payload: any) => {
      const cause = payload?.cause || payload;
      const eventCourtRoomId =
        getId(payload?.courtRoomId) || getCauseRoomId(cause);

      if (
        eventCourtRoomId &&
        courtRoomData?.courtRoomId &&
        eventCourtRoomId !== getId(courtRoomData.courtRoomId)
      ) {
        return;
      }

      if (!isTodaysCause(cause)) return;

      setAllCauses((prev) => mergeCause(prev, cause));
      setCauses((prev: any) =>
        mergeCause(Array.isArray(prev) ? prev : [], cause),
      );

      const query = debouncedQueryRef.current;
      if (query && matchesSearch(cause, query)) {
        setAllResults((prev) => mergeCause(prev, cause));
      }

      toast.success("New case added to cause list", {
        id: `new-cause-${cause?.cnr || getCauseKey(cause)}`,
      });
    };
    const handleCauseListUpdated = (payload: any) => {
      const cause = payload?.cause || payload;
      const eventCourtRoomId =
        getId(payload?.courtRoomId) || getCauseRoomId(cause);
      const belongsToCurrentRoom =
        !eventCourtRoomId ||
        !courtRoomData?.courtRoomId ||
        eventCourtRoomId === getId(courtRoomData.courtRoomId);
      const shouldShow = belongsToCurrentRoom && isTodaysCause(cause);

      const syncList = (prev: any[]) =>
        shouldShow ? mergeCause(prev, cause) : removeCause(prev, cause);

      setAllCauses(syncList);
      setAllResults((prev) =>
        shouldShow && matchesSearch(cause, debouncedQueryRef.current)
          ? mergeCause(prev, cause)
          : removeCause(prev, cause),
      );
      setCauses((prev: any) => syncList(Array.isArray(prev) ? prev : []));
    };
    const handleCauseListDeleted = (payload: any) => {
      const eventCourtRoomId = getId(payload?.courtRoomId);

      if (
        eventCourtRoomId &&
        courtRoomData?.courtRoomId &&
        eventCourtRoomId !== getId(courtRoomData.courtRoomId)
      ) {
        return;
      }

      setAllCauses((prev) => removeCause(prev, payload));
      setAllResults((prev) => removeCause(prev, payload));
      setCauses((prev: any) =>
        removeCause(Array.isArray(prev) ? prev : [], payload),
      );

      if (
        selectedCase &&
        ((payload?.causeId &&
          getId(selectedCase?._id) === getId(payload.causeId)) ||
          (payload?.cnr && selectedCase?.cnr === payload.cnr))
      ) {
        setSelectedCase(null);
      }
    };

    socket.on("case-selected", handleCaseSelected);
    socket.on("hearing-status-updated", handleHearingStatusUpdated);
    socket.on("cause-list-created", handleCauseListCreated);
    socket.on("cause-list-updated", handleCauseListUpdated);
    socket.on("cause-list-deleted", handleCauseListDeleted);
    return () => {
      socket.off("case-selected", handleCaseSelected);
      socket.off("hearing-status-updated", handleHearingStatusUpdated);
      socket.off("cause-list-created", handleCauseListCreated);
      socket.off("cause-list-updated", handleCauseListUpdated);
      socket.off("cause-list-deleted", handleCauseListDeleted);
    };
  }, [
    courtRoomData?.courtRoomId,
    refreshTimes,
    selectedCase,
    setActiveRecordingCnr,
    setCauses,
    setIsStreaming,
    setRecording,
    setRecordingStartedAt,
    setSelectedCase,
    setStreamingStartedAt,
    userData?._id,
  ]);
  return (
    <div className="hidden lg:block w-60 flex-shrink-0 h-full">
      <div
        className="bg-white border-r border-gray-200 h-full  shadow-sm 
      flex flex-col overflow-hidden"
      >
        <div className="shrink-0 px-2 py-1.5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
          <h3 className="font-bold text-blue-900 flex items-center text-sm">
            <FileText size={16} className="mr-2" />
            CAUSE LIST
          </h3>
        </div>
        <div className="shrink-0 bg-white px-2 py-1.5 border-b border-gray-100">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by CNR..."
              aria-label="Search cases by CNR"
              className="w-full pl-7 pr-7 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
            />
            {searchQuery && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          <div
            className="relative isolate mt-1 grid grid-cols-2 rounded-full bg-gray-100 p-0.5"
            role="group"
            aria-label="Cause list view"
          >
            <span
              aria-hidden="true"
              className={`pointer-events-none absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-full bg-white shadow-sm transition-transform duration-300 ease-in-out motion-reduce:transition-none ${listView === "passover" ? "translate-x-full" : "translate-x-0"}`}
            />
            {(["daily", "passover"] as const).map((view) => (
              <button
                key={view}
                type="button"
                aria-pressed={listView === view}
                onClick={() => {
                  setListView(view);
                  setExpandedCase(null);
                  scrollContainerRef.current?.scrollTo({ top: 0 });
                }}
                className={`relative z-10 rounded-full px-2 py-1 text-xs font-semibold transition-colors duration-300 motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-blue-500 ${listView === view ? "text-blue-700" : "text-gray-500 hover:text-gray-800"}`}
              >
                <span className="inline-flex items-center justify-center gap-1">
                  {view === "daily" ? "Daily List" : "Passover"}
                  <span
                    aria-label={`${viewCounts[view]} cases`}
                    className={`min-w-[1.25rem] rounded-full px-1.5 text-[10px] leading-4 tabular-nums ${
                      view === "passover"
                        ? "bg-amber-100 text-amber-800"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {viewCounts[view]}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
        <div
          className="min-h-0 space-y-1 p-1.5 overflow-y-auto flex-1"
          ref={scrollContainerRef}
        >
          {displayCauses?.map((cause: any, index: number) => (
            <div
              key={getCauseKey(cause)}
              className={`px-2 py-1.5 border rounded-md transition-colors ${
                cause.hearingStatus === "Completed"
                  ? "border-green-300 bg-green-100"
                  : cause.hearingStatus === "Passover"
                    ? "border-amber-300 bg-amber-100"
                    : cause.hearingStatus === "Active"
                      ? "border-blue-400 bg-blue-50"
                      : "border-yellow-300 bg-yellow-50"
              }`}
            >
              <div className="flex items-center justify-between gap-1">
                <span
                  title={cause.cnr}
                  className="min-w-0 text-sm font-semibold text-gray-800 truncate"
                >
                  <b>
                    {index + 1}
                    {". "}
                  </b>{" "}
                  {cause.cnr}
                </span>
                <HearingTimer
                  cnr={cause.cnr}
                  startAt={getHearingTimes(cause).startAt}
                  endAt={getHearingTimes(cause).endAt}
                  videoDuration={cause.videoDuration}
                  isActive={cause.hearingStatus === "Active"}
                  serverOffsetMs={serverOffsetMs}
                  className="text-xs"
                />

                <button
                  type="button"
                  aria-label={`Case information for ${cause.cnr}`}
                  aria-expanded={expandedCase === getCauseKey(cause)}
                  aria-controls={`case-info-${getCauseKey(cause)}`}
                  title="Case information"
                  onClick={() =>
                    setExpandedCase(
                      expandedCase === getCauseKey(cause)
                        ? null
                        : getCauseKey(cause),
                    )
                  }
                  className="ml-1 shrink-0 rounded-full p-1 text-blue-600 hover:bg-blue-100 focus-visible:outline-2 focus-visible:outline-blue-500"
                >
                  <Info size={14} />
                </button>
              </div>
              <div className="text-xs text-gray-600 leading-4 truncate">
                {cause.petitioner ||
                  cause.parties?.find((p: any) => p.type === "petitioner")
                    ?.name ||
                  ""}{" "}
                vs{" "}
                {cause.respondent ||
                  cause.parties?.find((p: any) => p.type === "respondent")
                    ?.name ||
                  ""}
              </div>
              {expandedCase === getCauseKey(cause) && (
                <dl
                  id={`case-info-${getCauseKey(cause)}`}
                  className="mt-2 space-y-2 rounded-md bg-gray-50 p-2 text-xs"
                >
                  {[
                    ["CNR", cause.cnr],
                    ["Case number", cause.caseNumber],
                    ["Case type", cause.caseType],
                    ["Case stage", cause.caseStage],
                    ["Petitioner", cause.petitioner],
                    ["Respondent", cause.respondent],
                    ["Petitioner advocate", cause.advocatePetitioner],
                    ["Respondent advocate", cause.advocateRespondent],
                    ["Hearing date", toDateKey(cause.hearingDate)],
                    ["Hearing status", cause.hearingStatus || "Pending"],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-gray-500">{label}</dt>
                      <dd className="break-words font-medium text-gray-800">
                        {value || "—"}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
              <div className="mt-1 flex flex-nowrap items-center gap-1">
                <span
                  title={cause.hearingStatus === "Active" ? "In progress" : cause.hearingStatus || "Pending"}
                  className={`mr-auto min-w-0 truncate text-[10px] font-semibold ${
                    cause.hearingStatus === "Completed"
                      ? "text-green-800"
                      : cause.hearingStatus === "Passover"
                        ? "text-amber-900"
                        : cause.hearingStatus === "Active"
                          ? "text-blue-700"
                          : "text-yellow-900"
                  }`}
                >
                  {cause.hearingStatus === "Active"
                    ? "In progress"
                    : cause.hearingStatus || "Pending"}
                </span>
                {!(
                  selectedCase?.cnr === cause.cnr &&
                  cause.hearingStatus === "Active"
                ) && (
                  <button
                    type="button"
                    disabled={!!pendingPassover || !!pendingStart}
                    onClick={() => handleCaseSelect(cause)}
                    className="flex shrink-0 items-center justify-center gap-0.5 whitespace-nowrap rounded bg-blue-600 px-1 py-1 text-[11px] font-semibold text-white hover:bg-blue-700 focus-visible:outline-2 focus-visible:outline-blue-700 disabled:opacity-50"
                  >
                    <Play size={12} />
                    {pendingStart === cause.cnr
                      ? "Starting…"
                      : cause.hearingStatus === "Completed"
                        ? "Start again"
                        : "Start"}
                  </button>
                )}
                {cause.hearingStatus === "Passover" && (
                  <button
                    type="button"
                    disabled={!!pendingPassover || !!pendingStart}
                    onClick={() => handleRemovePassover(cause)}
                    aria-label={`Remove ${cause.cnr} from Passover`}
                    title="Remove from Passover"
                    className="flex shrink-0 items-center justify-center rounded border border-red-300 bg-red-50 p-1 text-red-700 hover:bg-red-100 focus-visible:outline-2 focus-visible:outline-red-600 disabled:opacity-50"
                  >
                    <X size={13} />
                  </button>
                )}
                {cause.hearingStatus !== "Passover" && (
                  <button
                    type="button"
                    disabled={!!pendingPassover || !!pendingStart}
                    onClick={() => setConfirmPassover(cause)}
                    className="flex shrink-0 items-center justify-center gap-0.5 whitespace-nowrap rounded border border-amber-300 bg-amber-50 px-1 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-200 focus-visible:outline-2 focus-visible:outline-amber-700 disabled:opacity-50"
                  >
                    <SkipForward size={12} />
                    {pendingPassover === cause.cnr ? "Moving…" : "Passover"}
                  </button>
                )}
              </div>
            </div>
          ))}
          {(loading || isFetchingMore) && (
            <div className="text-center text-xs text-gray-400 py-2">
              Loading...
            </div>
          )}
          {!loading && !isFetchingMore && displayCauses.length === 0 && (
            <p
              className="px-2 py-6 text-center text-xs text-gray-500"
              role="status"
            >
              {hasMore
                ? "No cases in this view on the loaded pages. Load more to continue."
                : debouncedQuery
                  ? "No matching cases in this list."
                  : listView === "passover"
                    ? "No passed-over cases."
                    : "No cases in the daily list."}
            </p>
          )}
          {!loading && !isFetchingMore && hasMore && (
            <button
              type="button"
              onClick={() =>
                debouncedQuery
                  ? fetchSearch(debouncedQuery, searchPageRef.current + 1)
                  : fetchCauses(causePageRef.current + 1)
              }
              className="w-full rounded-md py-2 text-xs font-medium text-blue-600 hover:bg-blue-50"
            >
              Load more cases
            </button>
          )}
        </div>
      </div>
      {confirmPassover && (
        <FloatingPortal>
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <button
              type="button"
              aria-label="Close"
              tabIndex={-1}
              className="absolute inset-0 cursor-default bg-black/40"
              onClick={() => setConfirmPassover(null)}
            />
            <dialog
              open
              aria-modal="true"
              aria-labelledby="passover-confirm-title"
              className="relative m-0 w-full max-w-sm rounded-lg bg-white p-4 shadow-xl"
            >
              <h2
                id="passover-confirm-title"
                className="text-base font-semibold text-gray-900"
              >
                Move case to Passover?
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                <span className="font-semibold text-gray-800">
                  {confirmPassover.cnr}
                </span>{" "}
                will move to the Passover list.
                {confirmPassover.hearingStatus === "Active" &&
                  " The hearing timer and recording will stop."}
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  autoFocus
                  onClick={() => setConfirmPassover(null)}
                  className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const c = confirmPassover;
                    setConfirmPassover(null);
                    handlePassover(c);
                  }}
                  className="rounded bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 focus-visible:outline-2 focus-visible:outline-amber-700"
                >
                  Passover
                </button>
              </div>
            </dialog>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default MeetingCauseList;
